import i18next, {t} from 'i18next';
import {useEffect, useState} from 'react';
import {
  CampaignsGetResponse_Campaign,
  CEventLinkFlag,
  CLandingpageColorKey,
  CLandingpageMediaKey
} from 'src/apis/api-client';
import {Button} from 'primereact/button';
import {Image} from 'primereact/image';
import {joinCampaign} from 'src/shared/redirect-utils';

export type LandingPageProps = {
  campaignData: CampaignsGetResponse_Campaign;
}

export default function LandingPage(props: LandingPageProps) {
  const { campaignData } = props;

  const [ facebookLink, setFacebookLink ] = useState<string | undefined>();
  const [ youtubeLink, setYouTubeLink ] = useState<string | undefined>();
  const [ instagramLink, setInstagramLink ] = useState<string | undefined>();

  const [ imageGimmick, setImageGimmick ] = useState<string>('/images/defaults/gimmick.png');
  const [ imageHeader, setImageHeader ] = useState<string>('/images/defaults/header.jpg');
  const [ imageSquare, setImageSquare ] = useState<string>('/images/defaults/square.jpg');

  const [ headerTextColor, setHeaderTextColor ] = useState<string>('#FFFFFF');
  const [ headerButtonColor, setHeaderButtonColor ] = useState<string>('#FFFFFF');
  const [ headerHighlightColor, setHeaderHighlightColor ] = useState<string>('#ffe000');

  const [ factsBackgroundColor, setFactsBackgroundColor ] = useState<string>('#000000');
  const [ factsIconColor, setFactsIconColor ] = useState<string>('#FFFFFF');
  const [ factsTextColor, setFactsTextColor ] = useState<string>('#FFFFFF');

  const [ ctaBackgroundColor, setCtaBackgroundColor ] = useState<string>('#404040');
  const [ ctaTextColor, setCtaTextColor ] = useState<string>('#FFFFFF');
  const [ ctaButtonColor, setCtaButtonColor ] = useState<string>('#FFFFFF');
  const [ ctaSubtextColor, setCtaSubtextColor ] = useState<string>('#FFFFFF');
  const [ ctaHighlightColor, setCtaHighlightColor ] = useState<string>('#000000');

  const [ footerBackgroundColor, setFooterBackgroundColor ] = useState<string>('#000000');
  const [ footerTextColor, setFooterTextColor ] = useState<string>('#FFFFFF');

  /**
   * Initiate a timing function to let the tab title blink.
   */
  useEffect(
    () => {
      let chg : boolean = false;
      setInterval(() => {
        if (!chg) {
          document.title = t('landingPage.title', {eventName: campaignData.event?.title});
          chg = true;
        } else {
          document.title = t('landingPage.slogan', {eventName: campaignData.event?.title});
          chg = false;
        }
      }, 1000);
    }
  );

  /**
   * Load the color and media data into the variables.
   */
  useEffect(
    () => {
      if (!campaignData)
        return;

      // Assign the color entries.
      if (campaignData.landingpage?.colors) {
        campaignData
          .landingpage
          .colors
          .forEach(
            (color) => {
              if (color.hexCode && color.key) {
                let colorHex = toHex(color.hexCode);
                switch (color.key) {
                  // Header colors.
                  case CLandingpageColorKey.HEADER_TEXT:
                    setHeaderTextColor(colorHex);
                    break;
                  case CLandingpageColorKey.HEADER_BUTTON:
                    setHeaderButtonColor(colorHex);
                    break;
                  case CLandingpageColorKey.HEADER_HIGHLIGHT_COLOR:
                    setHeaderHighlightColor(colorHex);
                    break;

                  // Facts colors.
                  case CLandingpageColorKey.FACTS_BACKGROUND:
                    setFactsBackgroundColor(colorHex);
                    break;
                  case CLandingpageColorKey.FACTS_ICON:
                    setFactsIconColor(colorHex);
                    break;
                  case CLandingpageColorKey.FACTS_TEXT:
                    setFactsTextColor(colorHex);
                    break;

                  // CTA colors.
                  case CLandingpageColorKey.CTA_BACKGROUND:
                    setCtaBackgroundColor(colorHex);
                    break;
                  case CLandingpageColorKey.CTA_TEXT:
                    setCtaTextColor(colorHex);
                    break;
                  case CLandingpageColorKey.CTA_BUTTON:
                    setCtaButtonColor(colorHex);
                    break;
                  case CLandingpageColorKey.CTA_SUBTEXT:
                    setCtaSubtextColor(colorHex);
                    break;
                  case CLandingpageColorKey.CTA_HIGHLIGHT_COLOR:
                    setCtaHighlightColor(colorHex);
                    break;

                  // Footer colors.
                  case CLandingpageColorKey.FOOTER_BACKGROUND:
                    setFooterBackgroundColor(colorHex);
                    break;
                  case CLandingpageColorKey.FOOTER_TEXT:
                    setFooterTextColor(colorHex);
                    break;
                }
              }
            }
          );
      }

      // Assign the media entries.
      if (campaignData.landingpage?.media) {
        campaignData
          .landingpage
          .media
          .forEach(
            (media) => {
              if (media.key && media.url) {
                switch (media.key)
                {
                  case CLandingpageMediaKey.HEADER:
                    setImageHeader(media.url);
                    break;
                  case CLandingpageMediaKey.BACKGROUND:
                    break;
                  case CLandingpageMediaKey.GIMMICK:
                    setImageGimmick(media.url);
                    break;
                  case CLandingpageMediaKey.SQUARE:
                    setImageSquare(media.url);
                    break;
                }
              }
            }
          );
      }

      if (campaignData.event?.links) {
        campaignData
          .event
          .links
          .forEach(
            (link) => {
              if (link.flag && link.value) {
                switch (link.flag) {
                  case CEventLinkFlag.FACEBOOK:
                    setFacebookLink(link.value);
                    break;
                  case CEventLinkFlag.INSTAGRAM:
                    setInstagramLink(link.value);
                    break;
                  case CEventLinkFlag.YOUTUBE:
                    setYouTubeLink(link.value);
                    break;
                }
              }
            }
          );
      }
    },
    [ campaignData ]
  );

  /**
   * Convert a input string to a hex color string (add # to the front).
   * @param input The input to convert, e.g. 000000 (black).
   */
  function toHex(input: string): string {
    return `#${input}`.toLowerCase();
  }

  /**
   * Renders the Header content.
   */
  function renderHeaderRow() {
    return <>
      <div
        className='flex md:flex-row flex-column pl-4 pr-4'
        style={
          {
            backgroundColor: 'black',
            backgroundImage: `url(${(imageHeader ?? "/images/header.jpg")})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            height: '75vh'
          }
        }
      >
        <div
          className='flex flex-1 flex-column align-content-center justify-content-center'
          style={
            {
              height: '75vh'
            }
          }
        >
          <div style={{padding: '32px'}}>
            {
              campaignData.event?.title
              &&
              <small className={'uppercase font-bold text-xl'}
                     style={{color: headerTextColor}}
              >
                {campaignData.event?.title}
              </small>
            }
            <h1
              className='text-5xl md:text-7xl mt-2'
              style={{color: headerTextColor}}
            >
              {t('landingPage.header.title')}
            </h1>
            <p
              className='text-2xl md:text-4xl mt-0'
              style={{color: headerTextColor}}
              dangerouslySetInnerHTML={{__html: t('landingPage.header.subTitle', {interpolation: {escapeValue: false}})}}
            >
            </p>
            <Button
              label={
                campaignData.program?.isPublished
                  ? t('landingPage.header.button')
                  : t('landingPage.header.comingSoon')
              }
              onClick={() => {
                if (
                    campaignData.program?.isPublished
                    && campaignData.program.id
                ) {
                  joinCampaign(campaignData.program.id);
                }
              }}
              disabled={!campaignData.program?.isPublished}
              style={{color: headerButtonColor, width: "auto", maxWidth: '450px'}}
              size='large'
              outlined
            />
          </div>
        </div>
        <div className='hidden xl:flex flex-1 flex-column align-items-center justify-content-center' style={{paddingBottom: '4px'}}>
          <Image src={imageGimmick} />
        </div>
      </div>
    </>;
  }

  /**
   * Renders the Facts content.
   */
  function renderFactsRow() {
    return <>
      {
        <div
            className='grid p-6'
            style={
              {
                backgroundColor: `${factsBackgroundColor}`,
                marginRight: '0',
                marginLeft: '0'
              }
            }
        >
          {/* Register Icon */}
          <div className='col-12 md:col-4 p-4 lg:col-offset-3 lg:col-2'>
            <div className='flex flex-column align-items-center text-center'>
              <svg className='rowIcon' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M0 0h24v24H0V0z' fill='none'/>
                <circle cx='11' cy='8' opacity='.3' r='2'/>
                <path d='M5 18h4.99L9 17l.93-.94C7.55 16.33 5.2 17.37 5 18z' opacity='.3'/>
                <path
                    d='M11 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm-1 12H5c.2-.63 2.55-1.67 4.93-1.94h.03l.46-.45L12 14.06c-.39-.04-.68-.06-1-.06-2.67 0-8 1.34-8 4v2h9l-2-2zm10.6-5.5l-5.13 5.17-2.07-2.08L12 17l3.47 3.5L22 13.91z'
                />
              </svg>
              <h3 style={{color: `${factsIconColor}`}}>{t('landingPage.features.register.title')}</h3>
              <p style={{color: `${factsTextColor}`}}>{t('landingPage.features.register.text')}</p>
            </div>
          </div>

          {/* Sell Icon */}
          <div className='col-12 md:col-4 p-4 lg:col-2'>
            <div className='flex flex-column align-items-center text-center'>
              <svg className='rowIcon' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M0 0h24v24H0V0z' fill='none'/>
                <path d='M15.55 11l2.76-5H6.16l2.37 5z' opacity='.3'/>
                <path
                    d='M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.37-.66-.11-1.48-.87-1.48H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2h7.45zM6.16 6h12.15l-2.76 5H8.53L6.16 6zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z'
                />
              </svg>
              <h3 style={{color: `${factsIconColor}`}}>{t('landingPage.features.sell.title')}</h3>
              <p style={{color: `${factsTextColor}`}}>{t('landingPage.features.sell.text')}</p>
            </div>
          </div>

          {/* Experience Icon */}
          <div className='col-12 md:col-4 p-4 lg:col-2'>
            <div className='flex flex-column align-items-center text-center'>
              <svg className='rowIcon' xmlns='http://www.w3.org/2000/svg' enable-background='new 0 0 24 24'
                   viewBox='0 0 24 24' fill='currentColor'>
                <g>
                  <rect fill='none' height='24' width='24'/>
                </g>
                <g>
                  <g>
                    <path
                        d='M18.49,9.88l0.08-0.85l0.18-1.95l-1.9-0.43l-0.84-0.19l-0.44-0.74l-0.99-1.68L12.79,4.8L12,5.14L11.21,4.8 L9.42,4.03L8.43,5.71L7.99,6.45L7.15,6.64l-1.9,0.43l0.18,1.94l0.08,0.85l-0.56,0.65l-1.29,1.48l1.29,1.47l0.56,0.65l-0.08,0.85 l-0.18,1.96l1.9,0.43l0.84,0.19l0.44,0.74l0.99,1.67l1.78-0.77L12,18.85l0.79,0.34l1.78,0.77l0.99-1.68l0.44-0.74l0.84-0.19 l1.9-0.43l-0.18-1.95l-0.08-0.85l0.56-0.65l1.29-1.47l-1.29-1.47L18.49,9.88z M10.09,16.72l-3.8-3.81l1.48-1.48l2.32,2.33 l5.85-5.87l1.48,1.48L10.09,16.72z'
                        opacity='.3'
                    />
                    <path
                        d='M23,11.99l-2.44-2.79l0.34-3.69l-3.61-0.82L15.4,1.5L12,2.96L8.6,1.5L6.71,4.69L3.1,5.5L3.44,9.2L1,11.99l2.44,2.79 l-0.34,3.7l3.61,0.82L8.6,22.5l3.4-1.47l3.4,1.46l1.89-3.19l3.61-0.82l-0.34-3.69L23,11.99z M19.05,13.47l-0.56,0.65l0.08,0.85 l0.18,1.95l-1.9,0.43l-0.84,0.19l-0.44,0.74l-0.99,1.68l-1.78-0.77L12,18.85l-0.79,0.34l-1.78,0.77l-0.99-1.67l-0.44-0.74 l-0.84-0.19l-1.9-0.43l0.18-1.96l0.08-0.85l-0.56-0.65l-1.29-1.47l1.29-1.48l0.56-0.65L5.43,9.01L5.25,7.07l1.9-0.43l0.84-0.19 l0.44-0.74l0.99-1.68l1.78,0.77L12,5.14l0.79-0.34l1.78-0.77l0.99,1.68l0.44,0.74l0.84,0.19l1.9,0.43l-0.18,1.95l-0.08,0.85 l0.56,0.65l1.29,1.47L19.05,13.47z'
                    />
                    <polygon points='10.09,13.75 7.77,11.42 6.29,12.91 10.09,16.72 17.43,9.36 15.95,7.87'/>
                  </g>
                </g>
              </svg>
              <h3 style={{color: `${factsIconColor}`}}>{t('landingPage.features.experience.title')}</h3>
              <p style={{color: `${factsTextColor}`}}>{t('landingPage.features.experience.text')}</p>
            </div>
          </div>
        </div>
      }
    </>;
  }

  /**
   * Renders the CTA content.
   */
  function renderCtaRow() {
    return <>
      <div
          className='grid'
          style={
            {
              marginRight: '0',
              marginLeft: '0',
              marginTop: '0',
              minHeight: '100vh'
            }
          }
      >
        <div
          className='col-12 lg:col-6'
          style={
            {
              backgroundImage: `url(${(imageSquare ?? "/images/defaults/square.jpg")})`,
              backgroundPosition: 'right top',
              backgroundSize: 'cover',
              minHeight: '50vh'
            }
          }
        />
        <div
          className='col-12 lg:col-6'
          style={
            {
              backgroundColor: `${ctaBackgroundColor}`
            }
          }
        >
          <div className='h-full flex flex-column justify-content-center'>
            <div style={{padding: '50px'}}>
              <p
                className='font-bold text-4xl md:text-6xl'
                style={{color: `${ctaTextColor}`}}
              >
                {t('landingPage.cta.title')}
              </p>
              <p
                className='text-3xl md:text-5xl'
                style={{color: `${ctaSubtextColor}`}}
                dangerouslySetInnerHTML={{__html: t('landingPage.cta.title2', {interpolation: {escapeValue: false}})}}
              />
              {/* TODO: Add a nice underline. */}
              <Button
                label={
                  campaignData.program?.isPublished
                      ? t('landingPage.cta.button')
                      : t('landingPage.cta.comingSoon')
                }
                onClick={() => {
                  if (
                      campaignData.program?.isPublished
                      && campaignData.program.id
                  ) {
                    joinCampaign(campaignData.program.id);
                  }
                }}
                disabled={!campaignData.program?.isPublished}
                style={{color: `${ctaButtonColor}`}}
                size='large'
                outlined
              />
              {/* TODO: Add a painted error gif to point towards the button. */}
            </div>
          </div>
        </div>
      </div>
    </>;
  }

  /**
   * Renders the Footer content.
   */
  function renderFooter() {
    return <>
      <div
          className='h-18rem'
          style={
            {
              backgroundColor: `${footerBackgroundColor}`
            }
          }
      >
        <div
            className='flex flex-column gap-2 pt-6 align-items-center'
            style={
              {
                marginTop: '0'
              }
            }
        >
          {/* Socials */}
          <div className='flex flex-row align-items-start text-left'>
            {
              facebookLink
              &&
              <>
                <a href={facebookLink} target='_blank'>
                  <svg className='socialIcon' xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' viewBox='0 0 24 24'>
                    <path
                        d='M19,3H5C3.895,3,3,3.895,3,5v14c0,1.105,0.895,2,2,2h7.621v-6.961h-2.343v-2.725h2.343V9.309 c0-2.324,1.421-3.591,3.495-3.591c0.699-0.002,1.397,0.034,2.092,0.105v2.43h-1.428c-1.13,0-1.35,0.534-1.35,1.322v1.735h2.7 l-0.351,2.725h-2.365V21H19c1.105,0,2-0.895,2-2V5C21,3.895,20.105,3,19,3z'></path>
                  </svg>
                </a>
              </>
            }
            {
              instagramLink
              &&
              <>
                <a href='#' target='_blank'>
                  <svg className='socialIcon' xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' viewBox='0 0 24 24'>
                    <path
                        d='M 8 3 C 5.239 3 3 5.239 3 8 L 3 16 C 3 18.761 5.239 21 8 21 L 16 21 C 18.761 21 21 18.761 21 16 L 21 8 C 21 5.239 18.761 3 16 3 L 8 3 z M 18 5 C 18.552 5 19 5.448 19 6 C 19 6.552 18.552 7 18 7 C 17.448 7 17 6.552 17 6 C 17 5.448 17.448 5 18 5 z M 12 7 C 14.761 7 17 9.239 17 12 C 17 14.761 14.761 17 12 17 C 9.239 17 7 14.761 7 12 C 7 9.239 9.239 7 12 7 z M 12 9 A 3 3 0 0 0 9 12 A 3 3 0 0 0 12 15 A 3 3 0 0 0 15 12 A 3 3 0 0 0 12 9 z'></path>
                  </svg>
                </a>
              </>
            }
            {
              youtubeLink
              &&
              <>
                <a href='#' target='_blank'>
                  <svg className='socialIcon' xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' viewBox='0 0 24 24'>
                    <path
                        d='M10.053,7.988l5.631,8.024h-1.497L8.566,7.988H10.053z M21,6v12	c0,1.657-1.343,3-3,3H6c-1.657,0-3-1.343-3-3V6c0-1.657,1.343-3,3-3h12C19.657,3,21,4.343,21,6z M17.538,17l-4.186-5.99L16.774,7	h-1.311l-2.704,3.16L10.552,7H6.702l3.941,5.633L6.906,17h1.333l3.001-3.516L13.698,17H17.538z'></path>
                  </svg>
                </a>
              </>
            }
          </div>

          {/* Imprint & Privacy */}
          <div className='flex flex-row align-items-center'>
            <Button
                label='EN'
                onClick={() => i18next.changeLanguage('en')}
                link
                style={{color: `${footerTextColor}`}}
            />
            <Button
                label='DE'
                onClick={() => i18next.changeLanguage('de')}
                link
                style={{color: `${footerTextColor}`}}
            />
            <Button
                label={t('landingPage.footer.imprint')}
                onClick={() => window.open('https://fiesta.club/imprint', '_blank')}
                link
                style={{color: `${footerTextColor}`}}
            />
            <Button
                label={t('landingPage.footer.privacy')}
                onClick={() => window.open('https://fiesta.club/private-policy', '_blank')}
                link
                style={{color: `${footerTextColor}`}}
            />
          </div>

          {/* Mission */}
          <div
            className='text-center'
            style={{color: `${footerTextColor}`}}
          >
            <p>{t('landingPage.footer.copyright', {currentYear: (new Date()).getFullYear()})}</p>
            {
              campaignData.event?.company
              &&
              <small>{t('landingPage.footer.mission', {mission: campaignData.event?.company})}</small>
            }
          </div>
        </div>
      </div>
    </>;
  }

  return <>
    <style>
      {
        `
        .rowIcon {
          width: 96px;
          height: 96px;
          fill: ${factsIconColor}!important;
        }

        .socialIcon {
          width: 24px;
          height: 24px;
          fill: ${footerTextColor}!important;
        }
        
        .highlight-underline-header {
          background-image: linear-gradient(0deg, ${headerHighlightColor}, ${headerHighlightColor});
          background-repeat: no-repeat;
          background-size: 100% 0.5em;
          background-position: 4px 88%;
        }
        
        .highlight-underline-cta {
          background-image: linear-gradient(0deg, ${ctaHighlightColor}, ${ctaHighlightColor});
          background-repeat: no-repeat;
          background-size: 100% 0.5em;
          background-position: 4px 88%;
        }
        `
      }
    </style>

    <title>{t('landingPage.title', {eventName: campaignData.event?.title})}</title>

    {/* First Row (Header) */}
    {renderHeaderRow()}

    {/* Second Row (Process) */}
    {renderFactsRow()}

    {/* Third Row (Call to Action) */}
    {renderCtaRow()}

    {/* Fourth Row (Footer) */}
    {renderFooter()}
  </>;
}